import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarClock, ChevronLeft, ChevronRight, Clock, Check, Video, MessageCircle, ArrowRight, CalendarCheck, Loader2, X, CalendarPlus } from "lucide-react";
import { nexusApi, apiError, getUser } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const fmtDayKey = (d) => d.toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" });
const fmtTime = (d) => d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

export default function NexusUser() {
  const navigate = useNavigate();
  const me = getUser() || {};
  const [cfg, setCfg] = useState(null);
  const [types, setTypes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [step, setStep] = useState(1);
  const [sel, setSel] = useState(null);
  const [slotsData, setSlotsData] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [activeDay, setActiveDay] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [form, setForm] = useState({ candidato: "", pais: "", whatsapp: "", padre: "", edad: "", referencia: "" });
  const [booking, setBooking] = useState(false);
  const [done, setDone] = useState(null);

  const load = () => {
    nexusApi.config().then(setCfg).catch(() => {});
    nexusApi.types().then(setTypes).catch(() => {});
    nexusApi.myBookings().then(setBookings).catch(() => {});
  };
  useEffect(load, []);

  const pickType = async (t) => {
    setSel(t); setStep(2); setChosen(null); setActiveDay(null); setSlotsData(null); setLoadingSlots(true);
    try {
      const d = await nexusApi.slots(t.id);
      setSlotsData(d);
    } catch (e) { toast.error(apiError(e)); }
    setLoadingSlots(false);
  };

  // Agrupa slots por día LOCAL del navegador
  const grouped = useMemo(() => {
    if (!slotsData?.slots) return {};
    const g = {};
    slotsData.slots.forEach((s) => {
      const d = new Date(s.start_utc);
      const key = d.toDateString();
      if (!g[key]) g[key] = { label: fmtDayKey(d), items: [] };
      g[key].items.push({ ...s, date: d });
    });
    return g;
  }, [slotsData]);

  const dayKeys = Object.keys(grouped);
  useEffect(() => { if (dayKeys.length && !activeDay) setActiveDay(dayKeys[0]); }, [dayKeys, activeDay]);

  const goForm = () => {
    setForm({
      candidato: `${me.nombre || ""} ${me.apellidos || ""}`.trim(),
      pais: me.pais || "", whatsapp: me.whatsapp || "", padre: "",
      edad: me.edad || "", referencia: me.referencia || "",
    });
    setStep(3);
  };

  const confirm = async () => {
    for (const [k, v] of Object.entries(form)) {
      if (!String(v).trim()) return toast.error("Completa todos los datos del formulario.");
    }
    setBooking(true);
    try {
      const r = await nexusApi.book({ meeting_type_id: sel.id, slot_start: chosen.start_utc, ...form });
      setDone(r);
      setStep(4);
      nexusApi.myBookings().then(setBookings).catch(() => {});
    } catch (e) { toast.error(apiError(e)); }
    setBooking(false);
  };

  const reset = () => { setStep(1); setSel(null); setChosen(null); setDone(null); load(); };

  const cancelBooking = async (id) => {
    if (!window.confirm("¿Cancelar esta reunión?")) return;
    try { await nexusApi.cancel(id); toast.success("Reunión cancelada"); nexusApi.myBookings().then(setBookings); }
    catch (e) { toast.error(apiError(e)); }
  };

  const downloadIcs = async (id) => {
    try {
      const blob = await nexusApi.ics(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `supera-nexus-${id.slice(0, 8)}.ics`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Archivo de calendario descargado");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="nexus-user" className="min-h-screen bg-[#F4F6F9]">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#1E6F5C] via-[#0A1C30] to-[#16324f] text-white">
        <div className="max-w-5xl mx-auto px-5 py-8 md:py-12">
          <button onClick={() => navigate("/mi-cuenta")} className="flex items-center gap-2 text-white/70 hover:text-white text-sm font-bold mb-6" data-testid="nexus-back">
            <ChevronLeft className="w-4 h-4" /> Mi cuenta
          </button>
          <div className="flex items-center gap-3">
            <span className="w-14 h-14 rounded-2xl bg-white/10 ring-1 ring-white/20 flex items-center justify-center">
              <CalendarClock className="w-7 h-7 text-[var(--gold)]" />
            </span>
            <div>
              <div className="text-xs font-bold tracking-[0.25em] text-[var(--gold)]">SUPERA NEXUS</div>
              <h1 className="text-2xl md:text-3xl font-extrabold">{cfg?.titulo || "Sistema de Reservación de reuniones Supera"}</h1>
            </div>
          </div>
          <p className="mt-4 text-white/70 max-w-2xl text-sm md:text-base">{cfg?.intro}</p>
          {cfg?.instrucciones?.length > 0 && (
            <div className="mt-5 grid sm:grid-cols-2 gap-2">
              {cfg.instrucciones.map((ins, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-white/80">
                  <span className="w-5 h-5 rounded-full bg-[var(--gold)] text-[#0A1C30] text-xs font-extrabold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                  {ins}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 py-8">
        {/* Mis reuniones */}
        {bookings.length > 0 && step === 1 && (
          <div className="mb-8" data-testid="nexus-my-bookings">
            <h2 className="text-lg font-extrabold text-[#0A1C30] mb-3 flex items-center gap-2"><CalendarCheck className="w-5 h-5 text-[#1E6F5C]" /> Mis reuniones</h2>
            <div className="grid md:grid-cols-2 gap-3">
              {bookings.map((b) => {
                const d = new Date(b.slot_start);
                const cancelled = b.status === "cancelada";
                return (
                  <div key={b.id} data-testid={`nexus-booking-${b.id}`} className={`rounded-2xl border p-4 bg-white ${cancelled ? "border-black/10 opacity-60" : "border-[#1E6F5C]/30"}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-extrabold text-[#0A1C30]">{b.meeting_type_nombre}</span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${cancelled ? "bg-gray-200 text-gray-500" : b.is_upcoming ? "bg-emerald-100 text-emerald-700" : "bg-black/10 text-[#6B7688]"}`}>
                        {cancelled ? "Cancelada" : b.is_upcoming ? "Próxima" : "Realizada"}
                      </span>
                    </div>
                    <div className="text-sm text-[#6B7688] mt-1 flex items-center gap-1"><Clock className="w-4 h-4" /> {d.toLocaleString("es-MX", { dateStyle: "long", timeStyle: "short" })}</div>
                    <div className="text-xs text-[#8a94a6] mt-0.5">Candidato: {b.candidato}</div>
                    {!cancelled && b.is_upcoming && (
                      <div className="flex gap-2 mt-3">
                        <button data-testid={`nexus-join-${b.id}`} onClick={() => navigate(b.room_url)} className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-xl bg-[#1E6F5C] text-white"><Video className="w-4 h-4" /> Entrar a la videollamada</button>
                        <button data-testid={`nexus-ics-${b.id}`} onClick={() => downloadIcs(b.id)} className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-xl bg-black/5 text-[#0A1C30]"><CalendarPlus className="w-4 h-4" /> Calendario</button>
                        <button onClick={() => cancelBooking(b.id)} className="text-xs font-bold px-3 py-2 rounded-xl bg-black/5 text-[#6B7688]">Cancelar</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Paso 1: tipos */}
        {step === 1 && (
          <div data-testid="nexus-step-types">
            <h2 className="text-lg font-extrabold text-[#0A1C30] mb-3">Elige el tipo de reunión</h2>
            {types.length === 0 && <p className="text-sm text-[#6B7688]">No hay reuniones disponibles por ahora.</p>}
            <div className="grid md:grid-cols-3 gap-4">
              {types.map((t) => (
                <button key={t.id} data-testid={`nexus-type-${t.id}`} onClick={() => pickType(t)} className="text-left rounded-2xl bg-white ring-1 ring-black/5 p-5 hover:shadow-xl transition-all group">
                  <span className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ background: (t.color || "#C9A227") + "22" }}>
                    <CalendarClock className="w-6 h-6" style={{ color: t.color || "#C9A227" }} />
                  </span>
                  <div className="text-base font-extrabold text-[#0A1C30]">{t.nombre}</div>
                  <div className="text-sm text-[#6B7688] mt-1 line-clamp-3">{t.descripcion}</div>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-xs font-bold text-[#6B7688] flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {t.duracion_min} min</span>
                    <span className="text-[#1E6F5C] flex items-center gap-1 text-sm font-bold">Agendar <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Paso 2: horarios */}
        {step === 2 && sel && (
          <div data-testid="nexus-step-slots">
            <button onClick={() => setStep(1)} className="flex items-center gap-2 text-[#6B7688] hover:text-[#0A1C30] text-sm font-bold mb-4"><ChevronLeft className="w-4 h-4" /> Cambiar tipo de reunión</button>
            <div className="rounded-2xl bg-white ring-1 ring-black/5 p-5">
              <div className="text-base font-extrabold text-[#0A1C30]">{sel.nombre}</div>
              <div className="text-sm text-[#6B7688] flex items-center gap-1 mt-1"><Clock className="w-4 h-4" /> {sel.duracion_min} min · horarios en tu zona local</div>
              {loadingSlots ? (
                <div className="flex items-center gap-2 text-sm text-[#6B7688] py-10 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Cargando horarios…</div>
              ) : dayKeys.length === 0 ? (
                <p className="text-sm text-[#6B7688] py-8 text-center">No hay horarios disponibles por el momento. Vuelve a intentar más tarde.</p>
              ) : (
                <div className="mt-4 grid md:grid-cols-[220px_1fr] gap-5">
                  {/* Días */}
                  <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
                    {dayKeys.map((k) => (
                      <button key={k} data-testid={`nexus-day-${k}`} onClick={() => setActiveDay(k)} className={`w-full text-left px-4 py-2.5 rounded-xl text-sm font-bold capitalize transition-colors ${activeDay === k ? "bg-[#0A1C30] text-white" : "bg-black/[0.03] text-[#0A1C30] hover:bg-black/5"}`}>
                        {grouped[k].label}
                      </button>
                    ))}
                  </div>
                  {/* Slots del día */}
                  <div>
                    <div className="text-sm font-bold text-[#0A1C30] capitalize mb-2">{activeDay ? grouped[activeDay]?.label : ""}</div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[380px] overflow-y-auto">
                      {activeDay && grouped[activeDay]?.items.map((s) => (
                        <button key={s.start_utc} data-testid={`nexus-slot-${s.start_utc}`} onClick={() => { setChosen(s); goForm(); }}
                          className="px-2 py-2.5 rounded-xl text-sm font-bold border border-[#1E6F5C]/40 text-[#1E6F5C] hover:bg-[#1E6F5C] hover:text-white transition-colors">
                          {fmtTime(s.date)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Paso 3: formulario */}
        {step === 3 && sel && chosen && (
          <div data-testid="nexus-step-form" className="max-w-xl mx-auto">
            <button onClick={() => setStep(2)} className="flex items-center gap-2 text-[#6B7688] hover:text-[#0A1C30] text-sm font-bold mb-4"><ChevronLeft className="w-4 h-4" /> Cambiar horario</button>
            <div className="rounded-2xl bg-white ring-1 ring-black/5 p-6">
              <div className="rounded-xl bg-[#1E6F5C]/10 p-3 mb-5">
                <div className="text-sm font-extrabold text-[#0A1C30]">{sel.nombre}</div>
                <div className="text-sm text-[#1E6F5C] font-bold flex items-center gap-1 mt-0.5 capitalize"><CalendarCheck className="w-4 h-4" /> {new Date(chosen.start_utc).toLocaleString("es-MX", { dateStyle: "full", timeStyle: "short" })}</div>
              </div>
              <h3 className="text-base font-extrabold text-[#0A1C30] mb-3">Datos del candidato/a</h3>
              <div className="space-y-3">
                {[
                  ["candidato", "Nombre del candidato/a"], ["pais", "País"], ["whatsapp", "WhatsApp de contacto"],
                  ["padre", "Nombre del padre o madre"], ["edad", "Edad"], ["referencia", "Número de referencia Supera"],
                ].map(([k, label]) => (
                  <div key={k}>
                    <Label className="text-xs">{label}</Label>
                    <Input data-testid={`nexus-field-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                  </div>
                ))}
              </div>
              <button data-testid="nexus-confirm" onClick={confirm} disabled={booking} className="mt-5 w-full flex items-center justify-center gap-2 text-sm font-extrabold px-4 py-3 rounded-xl bg-[var(--gold)] text-[#0A1C30] disabled:opacity-60">
                {booking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Confirmar reunión
              </button>
            </div>
          </div>
        )}

        {/* Paso 4: éxito */}
        {step === 4 && done && (
          <div data-testid="nexus-success" className="max-w-xl mx-auto text-center rounded-2xl bg-white ring-1 ring-black/5 p-8">
            <div className="w-16 h-16 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-4"><Check className="w-8 h-8" /></div>
            <h2 className="text-xl font-extrabold text-[#0A1C30]">¡Reunión confirmada!</h2>
            <p className="text-sm text-[#6B7688] mt-2">Te enviamos la confirmación por correo y notificación. La reunión será por videollamada en <b>Supera Connect</b>, con tu enlace único y privado.</p>
            <div className="rounded-xl bg-[#F4F6F9] p-4 mt-4 text-left text-sm">
              <div className="flex items-center gap-2 text-[#0A1C30] font-bold"><CalendarCheck className="w-4 h-4 text-[#1E6F5C]" /> {done.fecha_local} <span className="text-[#8a94a6] font-normal">(tu hora local)</span></div>
              <div className="text-xs text-[#8a94a6] mt-1">🇲🇽 {done.fecha_mexico} (hora de México)</div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 mt-5">
              <button data-testid="nexus-goto-room" onClick={() => navigate(done.room_url)} className="flex-1 flex items-center justify-center gap-2 text-sm font-bold px-4 py-3 rounded-xl bg-[#1E6F5C] text-white"><Video className="w-4 h-4" /> Ver mi sala de videollamada</button>
              {done.wa_link && (
                <a data-testid="nexus-wa" href={done.wa_link} target="_blank" rel="noreferrer" className="flex-1 flex items-center justify-center gap-2 text-sm font-bold px-4 py-3 rounded-xl bg-[#25D366] text-white"><MessageCircle className="w-4 h-4" /> Confirmar por WhatsApp</a>
              )}
            </div>
            <button data-testid="nexus-ics-success" onClick={() => downloadIcs(done.booking.id)} className="mt-3 w-full flex items-center justify-center gap-2 text-sm font-bold px-4 py-3 rounded-xl bg-[#0A1C30] text-white"><CalendarPlus className="w-4 h-4" /> Agregar a Google / Apple Calendar</button>
            <button onClick={reset} className="mt-4 text-sm font-bold text-[#6B7688] hover:text-[#0A1C30]">Agendar otra reunión</button>
          </div>
        )}
      </div>
    </div>
  );
}
